import { FC, useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { tooltipClasses, TooltipProps, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { ColorModeContext } from '@/theme';
import { MenuItem } from 'react-pro-sidebar';
import { useTranslation } from 'react-i18next';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '.ps-menu-button:hover': {
    color: `${theme.palette.secondary.light} !important`,
    backgroundColor: 'transparent !important',
  },
  '.ps-menu-button': {
    color: `#fff !important`,
  },
}));

const BigTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#fff' : '#222',
    color: theme.palette.mode === 'dark' ? '#000' : '#fff',
    fontSize: 16,
    padding: '12px 18px',
    boxShadow: theme.shadows[4],
    maxWidth: 300,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.mode === 'dark' ? '#fff' : '#222',
  },
}));

const SideBarToggleTheme: FC = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const { t } = useTranslation();

  useEffect(() => {
    const hasSeen = localStorage.getItem('HAS_SEEN_THEME_TOOLTIP');
    if (!hasSeen) {
      setIsTooltipOpen(true);
    }
  }, []);

  const handleCloseTooltip = () => {
    setIsTooltipOpen(false);
    localStorage.setItem('HAS_SEEN_THEME_TOOLTIP', 'true');
  };

  const handleThemeClick = () => {
    handleCloseTooltip();
    colorMode.toggleColorMode();
  };

  return (
    <BigTooltip
      open={isTooltipOpen}
      onClose={handleCloseTooltip}
      arrow
      placement={'right'}
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h5" style={{ fontWeight: '' }}>
            {t('themeTooltip')}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseTooltip();
            }}
            sx={{ color: 'inherit' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }>
      <Box component="span">
        <StyledMenuItem
          onClick={handleThemeClick}
          icon={theme.palette.mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}>
          <Typography marginLeft={2.5} variant="h5">
            {theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Typography>
        </StyledMenuItem>
      </Box>
    </BigTooltip>
  );
};
export default SideBarToggleTheme;
